import React from 'react';
import {navigationRef} from 'src/Nav/navigationHelpers';
import {useSelector} from 'react-redux';
import {getHighestCompletionParams} from '../Utils/huntFunctions_v2';
import useParams from './useParams';

import {cloudFlareOptimizeImage} from '../Utils/helpers';
import {logAttributesForSentryCrashlytics} from '../Utils/fireUtils';

// location id can come from nav params for classic hunts
// location id can also come from state for regular
export const useLocationId = () => {
  var locationId = null;

  // seeing if we have a navigation parmam location id
  var {locationId: navigationLocationId} = useParams();

  // checking currentGameView state
  const currentLocationId = useSelector(
    (state) => state.game_v2?.currentLocationId,
  );

  logAttributesForSentryCrashlytics('locationId', `${locationId || 'none'}`);

  return currentLocationId || navigationLocationId;
};

export const usePercentageComplete = () => {
  return useSelector((state) => {
    const locationList = state?.game_v2?.locationList || [];
    const locationsLength = locationList?.length;
    if (!locationsLength) {
      return 0;
    }
    const locations = state?.game_v2?.locations || [];
    const locationsComplete = locationList.filter(
      (key) => locations[key]?.completeTime,
    ).length;
    const challengesList = Object.values(state?.game_v2?.challenges || {});
    const challengesLength = challengesList?.length;
    const challengesComplete = challengesList.filter(
      (challenge) => challenge?.completeTime,
    ).length;
    const percentageComplete = Math?.floor(
      ((locationsComplete + challengesComplete) /
        (locationsLength + challengesLength)) *
        100,
    );

    const huntFinished = state?.groupInfo?.huntFinished;

    if (huntFinished) {
      return 100;
    }

    return Math?.round(percentageComplete);
  });
};

// in local challenges there is a location override
export const useLocation = () => {
  const {locationOverride} = useParams();

  let locationId = useLocationId();
  const locations = useSelector((state) => state.game_v2?.locations || []);

  // if location override returning
  if (locationOverride) {
    return locationOverride;
  }

  // if no location override checking state
  const location = (locationId && locations[locationId]) || {};

  logAttributesForSentryCrashlytics?.(
    'locationTitle',
    `${location?.title || location?.name || ''}`,
  );
  return location;
};

//functions to get the challenges
// challenge id can come from params or state depending on hunt type
export const useChallengeId = (listChallengeIdOverride = null) => {
  const {challengeId: navigationChallengeid} = useParams();

  const currentChallengeId = useSelector(
    (state) => state.game_v2?.currentChallengeId,
  );
  console.log('useChallengeId', {
    listChallengeIdOverride,
    navigationChallengeid,
    currentChallengeId,
  });

  const challengeId = navigationChallengeid || null;

  logAttributesForSentryCrashlytics?.('challengeId', `${challengeId || ''}`);

  return challengeId;
};

// override only used in the LocationChallengeListModal
export const useChallenge = (listChallengeIdOverride = null) => {
  console.log('getChallenge: fired');
  // getting the challenge Id
  const challengeId = useChallengeId(listChallengeIdOverride);

  // getting an overrid if there is one
  const {challengeOverride} = useParams();

  console.log('useChallenge data', {
    challengeId,

    challengeOverride,
  });

  // getting the challenges from the source
  const allChallenges = useSelector((state) => state.game_v2['allChallenges']);

  // global.devAlert(challengeOverride)
  // selecting the actual challenge
  const challenge =
    (challengeId && allChallenges[challengeId]) || challengeOverride || {};

  logAttributesForSentryCrashlytics?.(
    'challengeTask',
    `${challenge?.task || ''}`,
  );

  return challenge;
};

// debug info used in various spots in the app for dev
export const useGameDebugInfo = () => {
  return __DEV__ ? ` ${useLocationId()}-${useChallengeId()}` : '';
};

// checking if at the right location (not challenge)
// aka did the group move ahead without this use
export const useGoToNextChallenge = () => {
  const locationId = useLocationId();
  const huntFinished = useSelector((state) => state.groupInfo?.huntFinished);

  const goToNextChallenge = () => {
    // checking if the hunt is finished
    if (huntFinished) {
      console.log('HuntFinished navigate1');
      return navigationRef.navigate('HuntFinished');
    }

    // checking to see if the players have moved to the next challenge
    const {route, params, highestUnfinshedLocationId} =
      getHighestCompletionParams();
    console.log('checkIfRightLocation', {
      route,
      params,
      highestUnfinshedLocationId,
      locationId,
    });
    if (highestUnfinshedLocationId != locationId) {
      console.log('checkIfRightLocation: Wrong Location!!', route);
      return route && navigationRef.navigate(route, params);
    }
  };

  return goToNextChallenge;
};

// export const useCompletionPercent = () => {
//   const [completionPercent, setCompletionPercentage] = useState(0.0);
//   return completionPercent;
// };

// getting the imageSource for user feedback
export const useImageSource = () => {
  const theme = useSelector((state) => state.groupInfo?.theme);
  const huntType = useSelector((state) => state.groupInfo?.huntType);

  // happy ghost
  const ghostBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582432_original.png',
    ),
  };
  // happy trophy
  const trophyBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582538_original.png',
    ),
  };
  // happy drink image
  const drinkBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582527_original.png',
    ),
  };
  // happy flower image
  const flowersBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582514_original.png',
    ),
  };
  // happy bow image
  const bowAndArrowBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582497_original.png',
    ),
  };
  // happy present image
  const presentBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582553_original.png',
    ),
  };
  // happy snowman image
  const snowManBanner = {
    uri: cloudFlareOptimizeImage(
      'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648582564_original.png',
    ),
  };

  if (huntType === 'ghostHunt') {
    return ghostBanner;
  }
  if (huntType === 'barHunt') {
    return drinkBanner;
  }
  if (theme === 'Bachelorette') {
    return flowersBanner;
  }
  if (theme === 'Date Night') {
    return bowAndArrowBanner;
  }
  if (theme === 'Birthday') {
    return presentBanner;
  }
  if (theme === 'Holiday') {
    return snowManBanner;
  }
  return trophyBanner;
};
